import React, { useState } from 'react';
import { products, Product } from 'data/shop';
import Image from 'next/image';
// import image1 from 'images/Ecoflow/Delta/1front.png';
// import image2 from 'images/Ecoflow/Delta/2front.png';
// import image3 from 'images/Ecoflow/Delta/3back.png';
// import image4 from 'images/Ecoflow/Delta/4back.png';
// import image5 from 'images/Ecoflow/Delta/5left.png';
// import image6 from 'images/Ecoflow/Delta/6right.png';
import {
  IoIosArrowDroprightCircle,
  IoIosArrowDropleftCircle,
} from 'react-icons/io';
import { GetStaticProps } from 'next';
import FullWidthBarBreadcrumbs from '@/components/Breadcrumb/FullWidthBarBreadcrumbs';
import { ProductJsonLd } from 'next-seo';
import MetaTags from '@/components/Head/MetaTags';
import PaymentBadges from '@/components/Shop/PaymentBadges';

export const getStaticProps: GetStaticProps = async () => {
  // Find the product with the slug 'delta-pro-portabse-power-station'
  const selectedProduct = products.find(
    (product: Product) => product.slug === 'delta-pro-portable-power-station'
  );

  if (!selectedProduct) {
    return {
      notFound: true, // Return a 404 page if the product is not found
    };
  }

  return {
    props: {
      product: selectedProduct,
    },
  };
};

type Props = {
  product: Product;
};

const ProductPage = ({ product }: Props) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const images = product.images;

  const prevImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );
  };

  const nextImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === images.length - 1 ? 0 : prevIndex + 1
    );
  };

  return (
    <>
      <FullWidthBarBreadcrumbs
        pages={[
          {
            name: 'Shop',
            href: '/shop',
          },
          {
            name: product.title,
            href: '/shop/' + product.slug,
          },
        ]}
      />
      <MetaTags
        title={'EcoFlow ' + product.title}
        description={'Shop the EcoFlow ' + product.title + ' on Energypal'}
      />
      <ProductJsonLd
        productName={product.title}
        brand={'EcoFlow'}
        manufacturerName={'EcoFlow'}
        images={images}
        offers={[
          {
            price: parseInt(product.price, 10),
            highPrice: product.salePrice,
            url: `https://energypal.com/shop/${product.slug}`,
            priceCurrency: 'USD',
            priceValidUntil: '2025-01-01',
            itemCondition: 'https://schema.org/NewCondition',
            availability: 'https://schema.org/InStock',
            seller: {
              '@type': 'Organization',
              name: 'EnergyPal',
              url: 'https://energypal.com/shop',
            },
          },
        ]}
      />
      <div className="max-w-7xl mx-auto">
        <div className="h-auto flex flex-col md:flex-row md:w-full md:space-x-8">
          {/* Left half for main image and gallery */}
          <div className="flex-1 w-full">
            <div
              className="max-w-screen-xl mx-auto p-8 h-[450px] md:h-[600px] relative md:sticky md:top-12 mb-12"
              style={{}}
            >
              {/* Main image */}
              <Image
                src={images[currentImageIndex]}
                alt="Product"
                className="object-fit p-8"
                layout="fill"
                objectFit="cover"
              />
              {/* Left arrow */}
              <IoIosArrowDropleftCircle
                className="absolute top-1/2 left-0 transform -translate-y-1/2 w-10 h-10 hover:cursor-pointer opacity-70 text-slate-800"
                onClick={prevImage}
              />
              {/* Right arrow */}
              <IoIosArrowDroprightCircle
                onClick={nextImage}
                className="absolute top-1/2 right-0 transform -translate-y-1/2 w-10 h-12 hover:cursor-pointer opacity-70 text-slate-800"
              />
            </div>
            {/* Gallery */}
            <div className="flex justify-center relative p-4 md:sticky md:top-3/4">
              {images.map((image, index) => (
                <div
                  key={index}
                  className={` h-20 w-20 sticky top-1/4 ${
                    index === currentImageIndex ? 'border-indigo-500' : ''
                  }`}
                >
                  <Image
                    src={image}
                    alt={`Product Image ${index + 1}`}
                    className="object-cover border-2 border-transparent p-2 "
                    layout="fill"
                    objectFit="cover"
                    onClick={() => setCurrentImageIndex(index)}
                  />
                  {index === currentImageIndex && (
                    <div className="absolute inset-0 flex justify-center items-center border-[1px] border-black bg-opacity-50"></div>
                  )}
                </div>
              ))}
            </div>
          </div>
          {/* Right half for product details */}
          <div className="flex-1 p-8 flex flex-col pt-16 ">
            <div>
              {/* <div className="uppercase tracking-wide text-sm text-indigo-500 font-semibold">
              Product Category
            </div> */}
              <h2 className="text-gray-900 text-3xl font-semibold mb-2">
                {product.title}
              </h2>
              <span className="text-gray-900 font-semibold ml-2 text-sm p-1 rounded-md bg-green-200">
                Save ${product.savings}
              </span>
              <div className="flex items-center mb-0 pb-0">
                {/* <span className="text-gray-700">Price:</span> */}
                <span className="text-gray-900 font-semibold ml-2 text-lg">
                  {product.price}
                </span>
                <span className="text-gray-500 font-semibold ml-2 text-lg line-through p-1 rounded-md">
                  {product.salePrice}
                </span>
              </div>
              <span className="text-gray-500 font-bold text-sm mb-4 ml-2 pt-0">
                Tax Included + Free Shipping
              </span>
              {/* <h3 className="text-gray-700 text-lg font-medium ml-2 mb-2 mt-4">
                Quantity
              </h3>
              <QuantitySelector /> */}

              <a href={product.buyLink}>
                <button className="bg-primary text-white px-12 py-4 rounded-md hover:bg-indigo-600 mb-4 w-full mt-2 text-lg font-medium">
                  Buy Now
                </button>
              </a>
              <PaymentBadges />

              <hr />
              {/* Accordion for description, technical specs, and warranty info */}
              <p className="mt-4">{product.intro}</p>

              <ul className="list-disc ml-4 my-4">
                {product.points.map((point, index) => (
                  <li key={index}>{point}</li>
                ))}
              </ul>
              <Accordion
                warranty={product.warranty}
                specs={product.techStats}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProductPage;

interface AccordionProps {
  warranty: string | undefined;
  specs: string[];
}

const Accordion: React.FC<AccordionProps> = ({ warranty, specs }) => {
  const [isDescriptionOpen, setIsDescriptionOpen] = useState(false);
  const [isTechSpecsOpen, setIsTechSpecsOpen] = useState(false);
  const [isWarrantyInfoOpen, setIsWarrantyInfoOpen] = useState(false);

  const renderIcon = (isOpen: boolean) => {
    return isOpen ? (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-5 w-5 inline-block ml-auto"
        viewBox="0 0 20 20"
        fill="currentColor"
      >
        <path
          fillRule="evenodd"
          d="M3.707 5.293a1 1 0 0 1 1.414 0L10 10.586l4.793-4.793a1 1 0 0 1 1.414 1.414l-5.5 5.5a1 1 0 0 1-1.414 0l-5.5-5.5a1 1 0 0 1 0-1.414z"
          clipRule="evenodd"
        />
      </svg>
    ) : (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-5 w-5 inline-block ml-auto"
        viewBox="0 0 20 20"
        fill="currentColor"
      >
        <path
          fillRule="evenodd"
          d="M3 7a1 1 0 0 1 1-1h12a1 1 0 0 1 0 2H4a1 1 0 0 1-1-1zM3 11a1 1 0 0 1 1-1h8a1 1 0 0 1 0 2H4a1 1 0 0 1-1-1zM3 15a1 1 0 0 1 1-1h8a1 1 0 0 1 0 2H4a1 1 0 0 1-1-1z"
          clipRule="evenodd"
        />
      </svg>
    );
  };

  const description = (
    <div>
      <p>
        <strong>Power for any situation.</strong>
        <br />
        From tailgate power to extreme blackouts that last for days on end,
        DELTA Pro delivers up to 10.8kWh of capacity. With that, you&apos;re
        covered for any situation. That&apos;s the industry gold standard.
      </p>

      <br />

      <p>
        <strong>All the AC output you’ll need.</strong>
        <br />A single DELTA Pro unit packs a 3600W AC output, which can be
        expanded up to 4500W with X-Boost technology. Power 99.99% heavy-duty
        devices at home, outdoors, or at work.
      </p>

      <br />

      <p>
        <strong>The world’s fastest charging portable power station.</strong>
        <br />
        To reach record-breaking charging speeds at 6500W, combine AC, solar and
        smart generator together for multiple charging.
      </p>

      <br />

      <p>
        <strong>Fully charge in 1.8 hours</strong>
        <br />
        EcoFlow’s X-Stream charging technology isn&apos;t just fast, it&apos;s
        safe too. The battery management system ensures safety and longevity,
        while you charge DELTA Pro from a standard AC wall outlet within 2.7
        hrs, and from 240V outlet within 1.8 hrs*.
        <br />
        * US version (1800W at 2.7 hrs/3000W at 1.8 hrs)
        <br />
        International version (2900W at 1.9 hrs)
      </p>

      <br />

      <p>
        <strong>Charge up at thousands of EV stations worldwide.</strong>
        <br />
        For the first time ever with a portable power station, you can charge up
        at thousands of EV stations worldwide. This unique charging method gives
        you up to 3400W fast charging on the go.
      </p>

      <br />

      <p>
        <strong>Go renewable with 1600W max solar charging.</strong>
        <br />
        EcoFlow&apos;s suite of solar products give you a cost-effective
        renewable energy source to stay powered. You can use solar energy to
        charge DELTA Pro in just 4-8 hours. DELTA Pro has a wide voltage range
        from 11-150V, which makes it compatible with 90% of third-party solar
        panels with MC4 connectors.
        <br />
        And when the weather gets unpredictable, the smart maximum power point
        tracking (MMPT) automatically adjusts to the conditions, maximizing your
        solar generation at any time of the day
      </p>

      <br />

      <p>
        <strong>Emergency power with Smart Generator.</strong>
        <br />
        The EcoFlow Smart Generator serves as an emergency option that
        integrates with your DELTA Pro ecosystem. It auto-starts when your DELTA
        Pro hits a low level of charge, re-charges your DELTA Pro, and stops the
        engine all by itself once your DELTA Pro hits a full charge. Compared to
        a traditional gas generator, the EcoFlow Smart Generator features an
        efficient process that results in less energy loss and quicker charging
        and higher fuel efficiency when you charge DELTA Pro.
      </p>

      <br />

      <p>
        <strong>A battery that last for years on end.</strong>
        <br />
        DELTA Pro sports a brand-new LFP battery with 6500 cycles, which means
        you can use DELTA Pro for years and years before your unit reaches 50%
        of the original capacity. Also, EcoFlow’s battery management system
        provides real-time analysis and regulation of voltage, current, and
        temperature. This unique protection mechanism makes DELTA Pro an
        incredibly safe and efficient home battery.
      </p>

      <br />

      <p>
        <strong>Multiple ways to control & monitor.</strong>
        <br />
        The new EcoFlow app gives you full control over DELTA Pro. You can
        access your power settings, such as charging and discharging levels, AC
        charge speeds, and more. There are even options to adjust the battery
        level at which the EcoFlow Smart Generator turns on, and you can even
        turn on the DELTA Pro when it’s in standby mode.
        <br />
        What’s more, the dedicated DELTA Pro Remote Control allows you to turn
        on and control the DELTA Pro either through a wired connection or
        wirelessly. It features a vivid LCD screen that displays the same power
        information that you can find on the DELTA Pro.
      </p>
    </div>
  );

  return (
    <div className="w-full mx-auto">
      <div className=" rounded-md overflow-hidden">
        {/* Description Accordion */}
        <div>
          <input
            type="checkbox"
            id="accordion1"
            className="hidden"
            checked={isDescriptionOpen}
            onChange={() => setIsDescriptionOpen(!isDescriptionOpen)}
          />
          <label
            htmlFor="accordion1"
            className="block cursor-pointer bg-gray-100 hover:bg-gray-200 px-4 py-3 mb-1 rounded-t-md text-gray-800 font-semibold flex items-center"
          >
            Description {renderIcon(isDescriptionOpen)}
          </label>
          <div
            className={`overflow-hidden transition-all duration-500 ${
              isDescriptionOpen ? 'max-h-100' : 'max-h-0'
            }`}
          >
            <div className="px-4 py-2 text-gray-700">{description}</div>
          </div>
        </div>

        {/* Technical Specs Accordion */}
        <div>
          <input
            type="checkbox"
            id="accordion2"
            className="hidden"
            checked={isTechSpecsOpen}
            onChange={() => setIsTechSpecsOpen(!isTechSpecsOpen)}
          />
          <label
            htmlFor="accordion2"
            className="block cursor-pointer bg-gray-100 hover:bg-gray-200 px-4 py-3 mb-1 text-gray-800 font-semibold flex items-center"
          >
            Technical Specs {renderIcon(isTechSpecsOpen)}
          </label>
          <div
            className={`overflow-hidden transition-all duration-500 ${
              isTechSpecsOpen ? 'max-h-100' : 'max-h-0'
            }`}
          >
            <div className="px-4 py-2 text-gray-700">
              <ul className="list-disc pl-2">
                {specs.map((spec, index) => (
                  <li key={index}>{spec}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Warranty Info Accordion */}
        <div>
          <input
            type="checkbox"
            id="accordion3"
            className="hidden"
            checked={isWarrantyInfoOpen}
            onChange={() => setIsWarrantyInfoOpen(!isWarrantyInfoOpen)}
          />
          <label
            htmlFor="accordion3"
            className="block cursor-pointer bg-gray-100 hover:bg-gray-200 px-4 py-3 mb-2 rounded-b-md text-gray-800 font-semibold flex items-center"
          >
            Warranty {renderIcon(isWarrantyInfoOpen)}
          </label>
          <div
            className={`overflow-hidden transition-all duration-500 ${
              isWarrantyInfoOpen ? 'max-h-100' : 'max-h-0'
            }`}
          >
            <div className="px-4 py-2 text-gray-700">{warranty}</div>
          </div>
        </div>
      </div>
    </div>
  );
};
